import React, { useState } from 'react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icons,
  useToast,
} from '@ais-ui/ui-core';
import { PiCopy, PiDotsThreeVertical } from 'react-icons/pi';
import { useLocalization } from '@ais-ui/locale-management';
import {
  ExportFormat,
  Job,
  ReportTemplate,
  useGetReportExportFormats,
  useGetReport,
} from '@ais-ui/websockets';
import { useTimeZoneContext } from '@ais-ui/utils';
import { useReportContext } from './ReportContext';
import { useExportReportSimple } from './useExportReportSimple';
import { ReportDelete } from './Mutations/DeleteReport';
import { AbortReport } from './Mutations/AbortReport';
import { CloneReport } from './Mutations/CloneReport/CloneReport';
import { usePermissions } from '../Permissions/PermissionsProvider';
import { useReportActions } from './useReportActions';

interface ReportItemDropDownMenuProps {
  report: Job;
  reportTemplate: ReportTemplate;
}

export const ReportItemDropDownMenu = ({
  report,
  reportTemplate,
}: ReportItemDropDownMenuProps) => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const permissions = usePermissions();
  const { selectedJob } = useReportContext();
  const { hideReport } = useReportActions();
  const { timeZone } = useTimeZoneContext();

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAbortDialog, setShowAbortDialog] = useState(false);
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);

  // Fetch export formats for this report type
  const { data: exportTypes } = useGetReportExportFormats(reportTemplate.type);

  // Fetch full report details for clone dialog (only when needed)
  const { data: reportDetails, isLoading: isLoadingReportDetails } = useGetReport(
    showCloneDialog ? report.uuid : '',
    timeZone
  );

  const { onExport, activeExportFormat } = useExportReportSimple(
    report.uuid,
    reportDetails?.info
  );

  const isFinished =
    report.status === 'FINISHED' || report.status === 'FINISHED_SCHEDULED';
  const canAbort = report.status === 'EXECUTING' || report.status === 'PENDING';
  const canDelete = report.status !== 'EXECUTING' && report.status !== 'PENDING';
  const hasClonePermission = permissions[reportTemplate.type];

  const handleExport = (format: ExportFormat) => {
    onExport(format);
  };

  const handleClone = () => {
    setPopoverOpen(false);
    setShowCloneDialog(true);
  };

  const handleAbort = () => {
    setPopoverOpen(false);
    setShowAbortDialog(true);
  };

  const handleDelete = () => {
    setPopoverOpen(false);
    setShowDeleteDialog(true);
  };

  const handleCopyId = async () => {
    await navigator.clipboard.writeText(report.uuid);
    toast({
      title: messages.genericToastTitleSuccess,
      description: messages.genericCopiedToClipboard,
    });
    setPopoverOpen(false);
  };

  return (
    <>
      <DropdownMenu open={popoverOpen} onOpenChange={setPopoverOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            id={`report-item-${report.uuid}-dropdown-button`}
            size="icon"
            variant="ghost"
            className="size-7"
            onClick={(e) => e.stopPropagation()}
          >
            <PiDotsThreeVertical className="size-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          {/* Clone */}
          <DropdownMenuItem
            onClick={handleClone}
            disabled={!hasClonePermission || !!activeExportFormat}
          >
            {messages.reportsDetailsActionsCloneText}
          </DropdownMenuItem>

          {/* Copy Report ID */}
          <DropdownMenuItem onClick={handleCopyId} className="justify-between">
            {messages.genericCopyId}
            <PiCopy className="size-4" />
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/* Export options - only show if finished */}
          {isFinished && exportTypes && exportTypes.length > 0 && (
            <>
              {exportTypes.includes('OF_PDF') && (
                <DropdownMenuItem
                  onClick={() => handleExport('OF_PDF')}
                  disabled={!!activeExportFormat}
                >
                  {activeExportFormat === 'OF_PDF' ? (
                    <>
                      <Icons.spinner className="mr-2 size-4 animate-spin" />
                      {messages.reportsDetailsActionsExportingPdfText}
                    </>
                  ) : (
                    messages.reportsDetailsActionsExportPdfText
                  )}
                </DropdownMenuItem>
              )}
              {exportTypes.includes('OF_CSV') && (
                <DropdownMenuItem
                  onClick={() => handleExport('OF_CSV')}
                  disabled={!!activeExportFormat}
                >
                  {activeExportFormat === 'OF_CSV' ? (
                    <>
                      <Icons.spinner className="mr-2 size-4 animate-spin" />
                      {messages.reportsDetailsActionsExportingCsvText}
                    </>
                  ) : (
                    messages.reportsDetailsActionsExportCsvText
                  )}
                </DropdownMenuItem>
              )}
              {exportTypes.includes('OF_EXCEL') && (
                <DropdownMenuItem
                  onClick={() => handleExport('OF_EXCEL')}
                  disabled={!!activeExportFormat}
                >
                  {activeExportFormat === 'OF_EXCEL' ? (
                    <>
                      <Icons.spinner className="mr-2 size-4 animate-spin" />
                      {messages.reportsDetailsActionsExportingExcelText}
                    </>
                  ) : (
                    messages.reportsDetailsActionsExportExcelText
                  )}
                </DropdownMenuItem>
              )}
              {exportTypes.includes('OF_ZIP') && (
                <DropdownMenuItem
                  onClick={() => handleExport('OF_ZIP')}
                  disabled={!!activeExportFormat}
                >
                  {activeExportFormat === 'OF_ZIP' ? (
                    <>
                      <Icons.spinner className="mr-2 size-4 animate-spin" />
                      {messages.reportsDetailsActionsExportingZipText}
                    </>
                  ) : (
                    messages.reportsDetailsActionsExportZipText
                  )}
                </DropdownMenuItem>
              )}
              {exportTypes.includes('OF_SHIPLIST') && (
                <DropdownMenuItem
                  onClick={() => handleExport('OF_SHIPLIST')}
                  disabled={!!activeExportFormat}
                >
                  {activeExportFormat === 'OF_SHIPLIST' ? (
                    <>
                      <Icons.spinner className="mr-2 size-4 animate-spin" />
                      {messages.reportsDetailsActionsExportingShiplistText}
                    </>
                  ) : (
                    messages.reportsDetailsActionsExportShiplistText
                  )}
                </DropdownMenuItem>
              )}
              {exportTypes.includes('OF_ESRI_SHAPES') && (
                <DropdownMenuItem
                  onClick={() => handleExport('OF_ESRI_SHAPES')}
                  disabled={!!activeExportFormat}
                >
                  {activeExportFormat === 'OF_ESRI_SHAPES' ? (
                    <>
                      <Icons.spinner className="mr-2 size-4 animate-spin" />
                      {messages.reportsDetailsActionsExportingShapefileText}
                    </>
                  ) : (
                    messages.reportsDetailsActionsExportShapefileText
                  )}
                </DropdownMenuItem>
              )}
              {exportTypes.includes('OF_ESRI_RASTER') && (
                <DropdownMenuItem
                  onClick={() => handleExport('OF_ESRI_RASTER')}
                  disabled={!!activeExportFormat}
                >
                  {activeExportFormat === 'OF_ESRI_RASTER' ? (
                    <>
                      <Icons.spinner className="mr-2 size-4 animate-spin" />
                      {messages.reportsDetailsActionsExportingRasterfileText}
                    </>
                  ) : (
                    messages.reportsDetailsActionsExportRasterfileText
                  )}
                </DropdownMenuItem>
              )}
              {exportTypes.includes('OF_CSV_TRACK') && (
                <DropdownMenuItem
                  onClick={() => handleExport('OF_CSV_TRACK')}
                  disabled={!!activeExportFormat}
                >
                  {activeExportFormat === 'OF_CSV_TRACK' ? (
                    <>
                      <Icons.spinner className="mr-2 size-4 animate-spin" />
                      {messages.reportsDetailsActionsExportingCsvTrackText}
                    </>
                  ) : (
                    messages.reportsDetailsActionsExportCsvTrackText
                  )}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
            </>
          )}

          {/* Abort - only for executing/pending */}
          {canAbort && (
            <DropdownMenuItem onClick={handleAbort}>
              {messages.reportsItemAbort}
            </DropdownMenuItem>
          )}

          {/* Delete */}
          <DropdownMenuItem
            onClick={handleDelete}
            disabled={!canDelete}
          >
            {messages.genericDelete}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Dialogs */}
      {showDeleteDialog && (
        <ReportDelete
          showDialog={showDeleteDialog}
          setShowDialog={setShowDeleteDialog}
          job={report}
          onBackToReports={() => {
            if (selectedJob?.uuid === report.uuid) {
              hideReport();
            }
          }}
        />
      )}
      {showAbortDialog && (
        <AbortReport
          showDialog={showAbortDialog}
          setShowDialog={setShowAbortDialog}
          job={report}
        />
      )}
      {showCloneDialog && (
        <CloneReport
          setShowPopover={setPopoverOpen}
          report={reportDetails}
          reportTemplate={reportTemplate}
          open={showCloneDialog}
          onOpenChange={setShowCloneDialog}
          isLoadingReport={isLoadingReportDetails}
        />
      )}
    </>
  );
};
